/* *
 * @gs/ui — Design Tokens GS-Apps
 * Source de vérité unique pour couleurs, espacement, typographie.
 * Inspiré de Google AI Studio — moderne, épuré, lisible.
 *
 *  NE JAMAIS hardcoder des valeurs CSS dans les composants.
 *      Utiliser ces tokens ou les classes Tailwind mappées.
 *
 * Version : v2.2.0 — 2026-06-10
 */

// ═══════════════════════════════════════════════════════════
// COULEURS
// ═══════════════════════════════════════════════════════════

export const GS_COLORS = {
  // Rouge GS officiel — Pantone 032 / RGB 217 34 49 / #D92231
  // Remplaçait l'orange #f97316 le 2026-04-01
  orange: {
    50:  '#fdf2f3',
    100: '#fce4e6',
    200: '#f9c9cd',
    300: '#f49aa0',
    400: '#ec606a',
    500: '#D92231',  // ← ROUGE GS OFFICIEL — Pantone 032
    600: '#b51c29',  // hover
    700: '#921622',  // active / texte
    800: '#701119',
    900: '#4d0c12',
  },
  red: {
    50:  '#fef2f2',
    100: '#fee2e2',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
  },
  blue: {
    50:  '#eff6ff',
    100: '#dbeafe',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
  },
  green: {
    50:  '#f0fdf4',
    100: '#dcfce7',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
  },
  amber: {
    50:  '#fffbeb',
    100: '#fef3c7',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
  },
  violet: {
    50:  '#f5f3ff',
    100: '#ede9fe',
    500: '#8b5cf6',
    600: '#7c3aed',
    700: '#6d28d9',
  },
  slate: {
    50:  '#f8fafc',  // fond sidebar
    100: '#f1f5f9',  // fond page content
    200: '#e2e8f0',  // bordures
    300: '#cbd5e1',
    400: '#94a3b8',  // texte désactivé / placeholder
    500: '#64748b',  // texte secondaire
    600: '#475569',  // texte corps
    700: '#334155',
    800: '#1e293b',  // texte principal
    900: '#0f172a',  // fond dark / navy GS
    950: '#020617',
  },
  // Surface de page (canvas) — MASTER-DS-STANDARD-VISUEL-GLOBAL 2026-06-17.
  // Canvas de page = plus PÂLE que slate-50 (#f8fafc) pour que les cartes bg-white
  // respirent. SEUL endroit où vit ce hex (miroir Tailwind = colors.page, DS-009).
  // S'applique AUX SEULES surfaces canvas (GsLayout <main> + écrans pleine page) —
  // JAMAIS les slate-50 d'ÉLÉMENT (sidebar, en-têtes, footers, hovers, chips).
  surface: {
    page: '#fbfbfb',  // bg-page / text-page (Tailwind) — fond de page global
  },
  white: '#ffffff',
  transparent: 'transparent',
} as const;

export const GS_SEMANTIC_COLORS = {
  navActiveBg:      'rgba(217,34,49,0.08)',  // bg-brand-500/8 — Rouge GS Pantone 032
  navActiveText:    GS_COLORS.orange[400],    // = #ec606a — Utiliser text-brand-400 en Tailwind
  navActiveBorder:  GS_COLORS.orange[500],    // = #D92231 — Utiliser border-brand-500 en Tailwind
  navHoverBg:       GS_COLORS.slate[50],
  listActiveBg:     GS_COLORS.blue[50],
  listActiveText:   GS_COLORS.blue[700],
  listActiveBorder: GS_COLORS.blue[500],
  statusOk:      { bg: GS_COLORS.green[100], text: GS_COLORS.green[700] },
  statusLate:    { bg: GS_COLORS.red[100],   text: GS_COLORS.red[700] },
  statusNever:   { bg: GS_COLORS.amber[100], text: GS_COLORS.amber[700] },
  statusInfo:    { bg: GS_COLORS.blue[100],  text: GS_COLORS.blue[700] },
  // Couleurs catégories articles (Recharts) — hex requis, Tailwind non supporté
  categorySig:   GS_COLORS.orange[500],  // Signalisation
  categoryEpi:   GS_COLORS.blue[500],    // EPI
  categoryLet:   GS_COLORS.violet[500],  // Lettrage
  roleAdmin:        { bg: GS_COLORS.violet[100], text: GS_COLORS.violet[700] },
  roleGestionnaire: { bg: GS_COLORS.blue[100],   text: GS_COLORS.blue[700] },
  roleUtilisateur:  { bg: GS_COLORS.slate[100],  text: GS_COLORS.slate[600] },
} as const;

// ═══════════════════════════════════════════════════════════
// TYPOGRAPHIE — OPTION B (Standard GS immuable v2.0.0)
// Choix validé : 2026-03-31 par David Collin
// Référence visuelle : Notion / Figma
// Modifier uniquement avec validation explicite de l'auteur
// ═══════════════════════════════════════════════════════════

export const GS_TYPOGRAPHY = {
  // —— Échelle de taille —————————————————————————————
  fontSize: {
    micro: '10px',  // badges compact, légendes
    xs:    '11px',  // en-têtes tableau, labels section, tooltips, sidebar section
    sm:    '13px',  // sous-titre page, texte secondaire
    base:  '14px',  // cellules tableau, body, inputs (← taille de base du contenu)
    md:    '15px',  // texte prominément
    lg:    '16px',  // réservé titres de section importants
    xl:    '18px',  // sous-titres modal
    '2xl': '22px',  // titre de page (← standard pageTitle)
    '3xl': '24px',  // valeur KPI (← standard kpiValue)
  },

  // —— Graisses —————————————————————————————————
  fontWeight: {
    normal:   '400',  // corps, cellules, sous-titres
    medium:   '500',  // chiffres (tabular), emphasis subtil
    semibold: '600',  // en-têtes tableau, badges, boutons
    bold:     '700',  // titres page, KPI valeurs, titres section
  },

  // —— Échelle de TAILLE seule (composable) — plancher remonté (MASTER-CONSOLE-LISIBILITE)
  // Règle #28 : le contenu des pages ne porte JAMAIS de `text-[Npx]` en dur — il
  // compose une de ces classes nommées (la TAILLE) avec sa couleur/graisse (tokens
  // Tailwind brand-*/slate-*). Les classes `text-chip`/`text-meta`/… sont définies
  // dans `tailwind.config.shared.js` (fontSize), MIROIR de cette échelle (source ici).
  // Fin de la « loupe » : plus de 10px comme taille de lecture courante — 10px (chip)
  // réservé aux badges/chips vraiment secondaires ; lecture courante 13-14px, méta 12px.
  size: {
    chip:  'text-chip',   // 10px — badge/chip secondaire UNIQUEMENT (plancher absolu)
    meta:  'text-meta',   // 12px — méta, labels secondaires (plancher de lecture)
    field: 'text-field',  // 13px — sous-titres, secondaire, sub, hints
    body:  'text-body',   // 14px — corps, cellules (lecture courante)
    lead:  'text-lead',   // 15px — texte proéminent
    head:  'text-head',   // 16px — titre de section/carte important
    title: 'text-title',  // 22px — titre de fiche/page
    kpi:   'text-kpi',    // 24px — valeur KPI / grand chiffre
  },

  // —— Hiérarchie complète — STANDARD OPTION B IMMUABLE ————————
  hierarchy: {
    // Pages
    pageTitle:    'text-title font-bold text-slate-800 dark:text-slate-100 leading-tight',
    pageSubtitle: 'text-field text-slate-500 dark:text-slate-400 mt-0.5',

    // Sections & Cards
    sectionTitle: 'text-sm font-semibold text-slate-700 dark:text-slate-200',
    cardTitle:    'text-sm font-semibold text-slate-700 dark:text-slate-200',
    sectionLabel: 'text-meta font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.10em]',

    // Formulaires
    fieldLabel:   'text-sm font-medium text-slate-700 dark:text-slate-200',
    fieldHint:    'text-xs text-slate-400 dark:text-slate-500',

    // Corps de texte
    body:         'text-body text-slate-700 dark:text-slate-200',
    secondary:    'text-field text-slate-500 dark:text-slate-400',
    meta:         'text-meta text-slate-400 dark:text-slate-500',

    // Tableaux
    tableHeader:  'text-meta font-semibold text-slate-600 dark:text-slate-300 tracking-wide',
    tableCell:    'text-body text-slate-700 dark:text-slate-200',
    tableNumber:  'text-body font-medium text-slate-700 dark:text-slate-200',  // tabular-nums à appliquer via classe
    tableEmpty:   'text-body text-slate-300 dark:text-slate-600',              // valeur "—" dans cellule

    // KPI & Metrics
    kpiValue:     'text-kpi font-bold text-slate-800 dark:text-slate-100 leading-none',
    kpiLabel:     'text-meta font-medium text-slate-500 dark:text-slate-400',

    // Badges
    badge:        'text-meta font-semibold',  // + couleur selon variante

    // Sidebar
    sidebarItem:  'text-meta text-slate-500 dark:text-slate-400',
    sidebarSection: 'text-chip font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-[0.12em]',

    // Tooltips
    // Legacy (surface SOMBRE) — ZERO consommateur (verdict CHORE-TOOLTIP-CONTENT-TOKENS).
    // La surface tooltip 2026 est CLAIRE (= GS_COMPONENTS.tooltip) ; valeur reconciliee
    // slate. Contenu riche = tooltipTitle/Description/Meta ci-dessous.
    tooltip:      'text-meta text-slate-600 dark:text-slate-300',
    // Tooltip riche (GsRichTooltipContent — surface claire 2026)
    tooltipTitle:       'text-field font-semibold text-slate-800 dark:text-slate-100',
    tooltipDescription: 'text-meta leading-snug text-slate-600 dark:text-slate-300',
    tooltipMeta:        'text-meta text-slate-400 dark:text-slate-500',

    // Badge déprécié — utiliser badge + variante de couleur
    badgeLabel:   'text-meta font-semibold text-slate-400 uppercase tracking-wider',
  },
} as const;

// ═══════════════════════════════════════════════════════════
// ESPACEMENT
// ═══════════════════════════════════════════════════════════

export const GS_SPACING = {
  pageGap:       'space-y-6',
  cardGap:       'gap-4',
  cardGapSm:     'gap-3',
  cardPadding:   'p-5',
  cardPaddingMd: 'p-4',
  cardPaddingSm: 'p-3',
  cellPadding:   'px-4 py-3',
  cellPaddingSm: 'px-3 py-2',
  btnPrimary:    'px-5 py-2.5',
  btnMobile:     'px-4 py-3.5',
  btnSecondary:  'px-4 py-2',
  inputPadding:  'px-3 py-2.5',
} as const;

// ═══════════════════════════════════════════════════════════
// DIMENSIONS SHELL — miroir tailwind.config.shared.js (BLITZ-LOT-B 2026-06-18)
// ═══════════════════════════════════════════════════════════

export const GS_DIMENSIONS = {
  sidebar: {
    collapsed: '3.25rem',  // w-sidebar-collapsed (52px)
    expanded:  '12rem',    // w-sidebar-expanded (192px)
  },
  toast: {
    maxWidth: '21.25rem', // max-w-toast (340px)
  },
  drawer: {
    mobileMaxWidth: '92vw', // max-w-drawer-mobile
  },
  field: {
    sm: '8.75rem',  // min-w-field-sm (140px)
    md: '12rem',    // min-w-field-md (192px)
    lg: '17.5rem',  // min-w-field-lg / max-w-field-lg (280px)
  },
  borderAccent: '3px', // border-l-3 (GsCategoryBanner)
  touchNavMinHeight: '3.75rem', // min-h-15 (60px)
  inset: {
    sidebarExpanded: '12rem', // left-sidebar-expanded (miroir w-sidebar-expanded)
  },
  diagram: {
    minWidth: '40rem', // min-w-diagram (640px — LineagePage)
  },
  splitSearch: {
    minHeight: '31.25rem', // min-h-sheet (500px — GS_LAYOUTS.splitSearch)
  },
} as const;

// ═══════════════════════════════════════════════════════════
// BORDER RADIUS
// ═══════════════════════════════════════════════════════════

export const GS_RADIUS = {
  sm:   'rounded',       // 4px  — badges compacts
  md:   'rounded-lg',    // 8px  — inputs, boutons desktop
  lg:   'rounded-xl',    // 12px ← STANDARD cards, panels
  xl:   'rounded-2xl',   // 16px — cards proéminentes, buttons terrain
  full: 'rounded-full',  // pill — badges, avatars
} as const;

// ═══════════════════════════════════════════════════════════
// OMBRES
// ═══════════════════════════════════════════════════════════

export const GS_SHADOWS = {
  card:     'shadow-sm',
  elevated: 'shadow-md',  // modals, menus (max autorisé)
  none:     'shadow-none',
  brand:    'shadow-sm shadow-brand-200',  // ← était 'orange', renommé v2.1.0
} as const;

// ═══════════════════════════════════════════════════════════
// COMPOSANTS — classes CSS complètes
// ═══════════════════════════════════════════════════════════

export const GS_COMPONENTS = {
  // Cards
  card:     'bg-white rounded-xl border border-slate-200 shadow-sm',
  cardLg:   'bg-white rounded-2xl border border-slate-100 shadow-sm',
  cardTerrain: (status: 'ok'|'retard'|'jamais') =>
    `bg-white rounded-2xl border-l-4 shadow-sm ${
      status === 'ok'     ? 'border-l-green-500' :
      status === 'retard' ? 'border-l-red-500'   : 'border-l-amber-400'
    }`,

  // Icônes colorées dans cards
  // 'brand' remplace 'orange' — ADR-015
  iconContainer: (color: 'brand'|'orange'|'blue'|'green'|'red'|'violet') =>
    `p-3 rounded-xl shrink-0 ${
      color === 'brand'  ? 'bg-brand-50'  :
      color === 'orange' ? 'bg-brand-50'  :  // alias rétrocompat
      color === 'blue'   ? 'bg-blue-50'   :
      color === 'green'  ? 'bg-green-50'  :
      color === 'red'    ? 'bg-red-50'    : 'bg-violet-50'
    }`,
  iconColor: (color: 'brand'|'orange'|'blue'|'green'|'red'|'violet') =>
    color === 'brand'  ? 'text-brand-600'  :
    color === 'orange' ? 'text-brand-600'  :  // alias rétrocompat
    color === 'blue'   ? 'text-blue-600'   :
    color === 'green'  ? 'text-green-600'  :
    color === 'red'    ? 'text-red-600'    : 'text-violet-600',

  // Boutons
  // btnPrimary + btnPrimaryMobile SONT DÉPRÉCIÉS — DÉCISION 2026-03-31
  // Utiliser btnAction (footer compact) ou btnActionMobile (pleine largeur mobile)
  // Les classes brand-* ci-dessous ne seront jamais appelées — tokens conservés pour compat
  btnPrimary:
    'px-5 py-2.5 bg-brand-500 hover:bg-brand-600 text-white font-medium ' +
    'rounded-lg text-sm transition-colors shadow-sm',  // DEPRECATED — voir btnAction
  btnPrimaryMobile:
    'w-full py-3.5 bg-brand-500 hover:bg-brand-600 active:bg-brand-700 ' +
    'text-white font-semibold rounded-xl text-base transition-colors ' +
    'shadow-sm shadow-brand-200 touch-manipulation flex items-center justify-center gap-2', // DEPRECATED — voir btnActionMobile

  // Nouveaux standards slate (DÉCISION 2026-03-31 — orange réservé sidebar uniquement)
  // Usage : footer sticky, modals, soumission
  btnAction:
    'px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 ' +
    'disabled:opacity-50 text-slate-800 dark:text-slate-100 text-field font-semibold rounded-lg transition-colors touch-manipulation',
  // Usage : CTA pleine largeur mobile (tableau de bord terrain, cards sites)
  btnActionMobile:
    'w-full py-3.5 bg-slate-800 hover:bg-slate-700 dark:bg-slate-600 dark:hover:bg-slate-500 ' +
    'text-white text-body font-semibold rounded-xl transition-colors ' +
    'touch-manipulation flex items-center justify-center gap-2',

  btnSecondary:
    'px-4 py-2 bg-transparent hover:bg-slate-100 dark:hover:bg-slate-700 ' +
    'text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 ' +
    'font-medium rounded-lg text-field transition-colors',
  btnGhost:
    'text-sm text-slate-500 hover:text-slate-700 touch-manipulation',
  btnDanger:
    'px-4 py-2 bg-red-500 hover:bg-red-600 text-white ' +
    'font-medium rounded-lg text-sm transition-colors',
  btnDangerOutline:
    'px-4 py-2 bg-transparent hover:bg-red-50 dark:hover:bg-red-950/30 ' +
    'text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900 ' +
    'font-semibold rounded-lg text-field transition-colors',
  // Bouton icône seul — UTILISER GsIconButton de @gs/ui
  // Standard GS v1.3 : toujours icône + tooltip, jamais texte dans header actions
  // Variantes header: secondary (blanc/bordure) ou ghost — JAMAIS primary (orange) dans PageHeader
  // primary (orange) réservé aux CTA dans le contenu de page uniquement
  // Tailles: sm (28px), md (32px), lg (36px — défaut header)
  btnIcon: 'inline-flex items-center justify-center rounded-lg transition-colors',

  // Inputs
  input:
    'w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-white ' +
    'focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400',
  inputSearch:
    'w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl bg-white ' +
    'shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400',
  // inputQty — cartes saisie inventaire (DS v2.0.1 — py-1.5 text-head rounded-lg)
  inputQty:
    'no-spinners flex-1 py-1.5 text-head font-bold text-center ' +
    'border border-slate-200 dark:border-slate-600 rounded-lg ' +
    'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 ' +
    'focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20',

  // Tableaux
  tableHeader:
    'sticky top-0 z-20 bg-white px-4 py-3 text-center text-xs font-bold ' +
    'text-slate-500 uppercase tracking-wide border-b border-slate-100 whitespace-nowrap',
  tableCell:
    'px-4 py-3 text-sm text-slate-700 border-b border-slate-50',
  tableCellCritical:
    'px-4 py-3 text-sm text-red-600 font-bold bg-red-50 border-b border-slate-50',
  tableCellEmpty:
    'px-4 py-3 text-sm text-slate-300 border-b border-slate-50 text-center',
  tableFooter:
    'sticky bottom-0 bg-slate-50 border-t-2 border-slate-200 ' +
    'px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide',

  // Listes sélectionnables
  listItem: (active: boolean) =>
    `w-full flex items-center justify-between px-4 py-3.5 text-left ` +
    `border-b border-slate-50 transition-colors last:border-b-0 ${
      active ? 'bg-blue-50 border-l-2 border-l-blue-500' : 'hover:bg-slate-50'
    }`,
  listItemTitle: (active: boolean) =>
    `text-sm font-medium truncate ${active ? 'text-blue-700' : 'text-slate-800'}`,

  // Contrôles +/− terrain — standard DS v2.0.0 (cartes saisie inventaire)
  // h-8 w-8 (32px), rounded-lg — compact, proportionnel au reste
  qtyMinus:
    'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ' +
    'border border-slate-200 dark:border-slate-600 ' +
    'bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-300 ' +
    'hover:bg-slate-50 dark:hover:bg-slate-600 ' +
    'text-base font-bold transition-colors touch-manipulation',
  qtyPlus:
    'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ' +
    'bg-slate-700 hover:bg-slate-600 dark:bg-slate-600 dark:hover:bg-slate-500 ' +
    'text-white text-base font-bold transition-colors touch-manipulation',

  // Tooltip — surface 2026 (GsTooltip de @gs/ui, rendu en portail Radix).
  // Fond clair eleve + bordure hairline + shadow-lg + rounded-lg, texte 13px.
  // z-50 en tete (exclusion DS-008) ; shadow-lg isole sur sa propre ligne pour
  // ne jamais cohabiter avec bg-white sur une meme ligne (regle DS-008).
  tooltip:
    'z-50 max-w-60 rounded-lg border border-slate-200 bg-white px-3 py-2 ' +
    'text-field leading-snug text-slate-700 ' +
    'shadow-lg dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200',
  tooltipArrow: 'fill-white dark:fill-slate-800',
  // Chip de la ligne meta du tooltip riche (conteneur, prefixe, statut) — canonique,
  // consomme par GsRichTooltipContent (P2 RoleMatrix) et tout futur appelant.
  tooltipChip:
    'inline-flex items-center rounded border border-slate-200 bg-slate-100 px-1.5 py-px ' +
    'text-chip font-medium text-slate-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300',
} as const;

// ═══════════════════════════════════════════════════════════
// LAYOUT PATTERNS
// ═══════════════════════════════════════════════════════════

export const GS_LAYOUTS = {
  // Pages de recherche — 40% liste / 60% détail (style Google AI Studio)
  splitSearch: 'flex gap-4 min-h-sheet',
  splitLeft:   'w-2/5 shrink-0',
  splitRight:  'flex-1 min-w-0',

  // Grille KPI — 4 colonnes desktop (tableau de bord)
  kpiGrid:       'grid grid-cols-2 lg:grid-cols-4 gap-4',
  kpiGridMobile: 'grid grid-cols-2 gap-3',

  // Grille financière — cards compactes par site (STANDARD GS)
  // Règles d'utilisation :
  //   - padding interne : p-3.5
  //   - valeur totale   : text-base font-bold
  //   - labels catégorie: text-meta
  //   - barres          : h-1.5
  financialGrid: 'grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3',

  // Layout sidebar filtres + contenu
  sidebarLayout: 'flex gap-5',
  sidebarPanel:  'hidden lg:block w-64 shrink-0',
  mainPanel:     'flex-1 overflow-auto',

  // Liste maître master-detail Console UG — MIROIR maxHeight.console-list (65vh)
  consoleListMaxHeight: 'max-h-console-list',
} as const;

// ═══════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════

export const GS_ANIMATIONS = {
  spinner:   'animate-spin',
  shimmer:   'skeleton-shimmer',
  pageEnter: 'page-enter',
  cardHover: 'card-hover',
  kpiHover:  'kpi-hover',
} as const;

// ═══════════════════════════════════════════════════════════
// BREAKPOINTS (usage JS/TS)
// ═══════════════════════════════════════════════════════════

export const GS_BREAKPOINTS = {
  sm:   '640px',
  md:   '768px',
  lg:   '1024px',
  xl:   '1280px',
  '2xl':'1536px',
} as const;

// ═══════════════════════════════════════════════════════════
// VERSION — mécanisme de mise à jour
// ═══════════════════════════════════════════════════════════

export const GS_DESIGN_SYSTEM_VERSION = {
  version: '2.3.22',
  date: '2026-07-14',
  author: 'David Collin, Groupe Signalisation',
  changelog: [
    "2.3.22 (2026-07-14) — CHORE-DS-SKELETON-SHARED : les classes du contrat @gs/ui (.skeleton-shimmer, .animate-dialog, .animate-dialog-backdrop) sont définies UNE fois dans tailwind.config.shared.js (plugin « @gs/ui — composants partagés », couleurs sourcées theme('colors.gs.slate.*'), keyframes shimmer/fadeIn/scaleIn au theme). Retrait des blocs locaux divergents (admin + gest : hex #e2e8f0/#f1f5f9 en dur ; hub : rgb() en dur) ; ug guérit (le bloc y était ABSENT → shimmer inerte). Zéro changement visuel (mêmes gris slate-200/100, mêmes timings 1.5s / 0.2s).",
    "2.3.21 (2026-06-26) — CHORE-DS015-SWEEP : tokens dimension inset sidebar-expanded (left-sidebar-expanded), min-w-diagram (640px), min-h-sheet (500px, GS_LAYOUTS.splitSearch) ; sweep 20 hits arbitraires restants (ui/admin/ug, feedback déjà f7c26bb) ; DS-015 flip bloquant (opt-out --warn-on-arbitrary-spacing). tooltip max-w-60.",
    "2.3.20 (2026-06-26) — MASTER-UG-PERSONNES-GABARITS-NAV (échelle d'action) : btnSecondary passe en contour (transparent + bordure slate, dark-aware) ; nouveau btnDangerOutline (destructif discret, contour rouge) ; btnAction tonal slate + btnActionMobile aplat inchangés depuis 2.3.19 ; Console UG TemplateDetailView Archiver → btnDangerOutline (plus btnDanger plein). Standard échelle d'action documenté UI_STANDARDS + COMPONENT_CATALOG.",
    "2.3.19 (2026-06-26) — MASTER-UG-PERSONNES-GABARITS-NAV : btnAction passe en tonal slate (fond clair + texte foncé) pour CTA secondaires discrets ; btnActionMobile inchangé (aplat slate terrain). Console UG : audit remplacement bg-slate-800 hardcodé par GS_COMPONENTS.btnAction.",
    "2.3.18 (2026-06-22) — MASTER-UG-CONFIG-FINITION-VISUELLE : GsToggle ON brand-500 (marque) / OFF slate-200 inchangé. Console UG ConfigurationTab : statut module inline muted (« Label · planifié »), colonne toggles alignée ; modules OFF label atténué ; retrait sous-titre fiche compagnie.",
    "2.3.17 (2026-06-22) — MASTER-UG-REDESIGN-F3-1-CONFIG-POLISH : GsToggle compact ~36×20px (h-5 w-9, pastille 16px). Console UG ConfigurationTab déplié, StatusBadge si statut ≠ actif ; AdminPickerPopover grant/revoke + recherche.",
    "2.3.16 (2026-06-22) — MASTER-UG-REDESIGN-F3-CONFIG : nouveau composant GsToggle (@gs/ui) — interrupteur slate ON slate-800 / OFF slate-200 (zéro green-*). Console UG ConfigurationTab : résumé replié par défaut, toggles entitlement groupés par catégorie + StatusBadge, mutation inline (retrait EntitlementDrawer). COMPONENT_CATALOG + UI_STANDARDS.",
    "2.3.13 (2026-06-19) — MASTER-UG-ROLEMATRIX-DELEGUE (PROMPT 3, ADR-067 D2/D4/D5) : nouveau PATTERN D'ECRAN « console deleguee ». Un ADMIN de compagnie (useMe().consoleAccessVia === 'delegated') voit la MEME RoleMatrix (INCHANGEE, scope-agnostique) mais SCOPEE : un BANDEAU DE SCOPE (accent = marque tenant ADR-059 ; compagnie verrouillee si une seule, mini-selecteur restreint aux compagnies administrees si plusieurs) REMPLACE le selecteur de compagnie corporate ; colonnes = membres de SA compagnie (/api/companies/:id/members), lignes = modules ENTITLES (ug.CompanyModules, /api/companies/:id/modules) et non les 15 ; conteneurs vides masques. Page app-local DelegatedConsoleView (apps/ug) montee par App.tsx quand le verdict serveur est 'delegated' ; le god-view corporate ('corporate'|'uca') reste STRICTEMENT INCHANGE. Tokens brand-*/slate- uniquement (regle #28), tailles via l'echelle nommee (text-chip..text-head) — zero hex. AUCUN token / composant @gs/ui neuf (consommation cote app, comme 2.3.2) — le bump trace le pattern d'ecran. Standard : UI_STANDARDS §4c (« console deleguee = bandeau de scope remplacant le switcher + matrice scopee membres x modules entitles + mini-selecteur restreint si multi-compagnies »). Mock fige David : UG-ROLEMATRIX-DELEGUE-MOCK-2026-06-19.html. Aucune mutation de palette ni de typo.",
    "2.3.12 (2026-06-18) — CHORE-DS-KW034-GATE-OVERLAY-AWARE : DS-007/008 conscients du contexte d'élément (overlay/input lookback) + ds-lint-exempt etendu aux RULES ; resorbe KW-034 (4 faux positifs). Zero composant touche.",
    "2.3.11 (2026-06-18) — BLITZ-LOT-G-RYTHME-UG : token max-h-console-list (65vh, tailwind maxHeight.console-list) unifie les listes master-detail Console UG (Users/Templates/Companies) ; retrait p-4 redondant (GsLayout padde deja) ; PageHeader aligne sur les 4 vues master-detail + Commentaires. UI_STANDARDS §4c rythme master-detail. Aucun composant @gs/ui neuf.",
    "2.3.10 (2026-06-18) — BLITZ-LOT-B-DIMENSIONS-TOKENS : tokenisation dimensions shell/admin/hub — sidebar-collapsed/expanded, max-w-toast, max-w-drawer-mobile, min-w-field-sm/md/lg, border-l-3 ; core size-8, min-w-11, min-h-15, rounded-xl, top-5/-right-3. GS_DIMENSIONS (tokens.ts). UI_STANDARDS regle dimensions shell. Perimetre @gs/ui + admin + hub (pas Console UG pages). Pas de gate DS-015 (Lot G parallele).",
    "2.3.9 (2026-06-18) — BLITZ-LOT-C-ACTIVITY-ROW : GsActivityRow (@gs/ui) ligne de feed unifiee densite Commentaires (text-meta, py-2.5, gap-2.5, marqueur h-5 icone ou h-2 pastille, horodatage text-chip). Refactor OverviewView Activite recente + JournalCard. COMPONENT_CATALOG + UI_STANDARDS §4c ligne de feed. Mock CONSOLE-UG-ACTIVITY-ROW-DENSITY-MOCK-2026-06-18.html.",
    "2.3.6 (2026-06-18) — BLITZ-DS-SHELL-PURGE-CONTOUR (Lots A+E+tuile) : purge zero hex shell @gs/ui (GsHeader, GsSidebar, GsUserMenu) mapping exact slate/brand ; bouton + header + nav mobile active -> brand-* ; etats running/seuils admin -> amber-* semantique ; KpiCard variant brand canonique + alias orange deprecated ; GsSearchInput inline left-2.5 pl-8 text-field ; RoleMatrix tuile ADMIN = contour marque (ring-inset). UI_STANDARDS §4c amende. Aucun nouveau token palette.",
    "2.3.4 (2026-06-18) — CHORE-HEADER-BG-PAGE (correctif du standard DS 2.3.3 — couture visible entre GsHeader bg-slate-50 et canvas bg-page #fbfbfb) : GsHeader (@gs/ui) bg-slate-50->bg-page (mode clair ; dark:bg-slate-900 inchange ; bordure border-b conservee — de-tokenisation = sweep DS-014 futur). UI_STANDARDS §4c « Standard visuel global » amende : la barre superieure GsHeader fait partie de la surface de page (bg-page), elle ne contraste pas ; GsSidebar reste slate-50 (panneau distinct). Aucun nouveau token (reutilise bg-page / GS_COLORS.surface.page existant — DS-009 inchange). Dette #28 residuelle GsHeader (bouton orange hardcode, #ececec, text-meta) = sweep DS-014, hors scope. Aucun composant @gs/ui neuf, zero DB/Azure.",
    "2.3.3 (2026-06-17) — MASTER-DS-STANDARD-VISUEL-GLOBAL (standard visuel GLOBAL, fige David : « le fond est global, les styles globaux ; dans toutes les pages les chiffres se fient a ce standard ») : 3 regles transverses actees dans UI_STANDARDS §4c (« Standard visuel global — fond, chiffres, matrice »). (a) Canvas de page = token bg-page (#fbfbfb, plus pale que slate-50 pour que les cartes bg-white respirent) — applique AUX SEULES surfaces canvas : GsLayout <main> + ecrans pleine page (GsLoginScreen, GsForbiddenScreen, GsErrorPage, GsErrorBoundary, ecrans login/loading/forbidden de apps/admin + apps/hub, body racine apps/admin) ; le slate-50 reste RESERVE aux ELEMENTS contrastants (sidebar, en-tetes/footers, coins de matrice, chips, hovers) — non touches. Le hex #fbfbfb vit UNE seule fois en source (GS_COLORS.surface.page) + miroir DS-009 (colors.page de tailwind.config.shared.js) ; dark:bg-slate-900 inchange. (b) Poids des chiffres = font-bold (700, = fontWeight.bold / hierarchy.kpiValue) — JAMAIS font-extrabold sur un chiffre : OverviewKpi (apps/ug) font-extrabold->font-bold ; KpiCard (@gs/ui) re-tokenise text-kpi->text-kpi + text-meta->text-meta (no-op visuel, purge des text-[Npx] en dur, regle #28). (c) Matrice d'etat = tuile a dimension CONSTANTE : RoleMatrix (apps/ug) — <table> sans w-full (largeur-contenu, alignee a gauche), colonne « qui » a largeur fixe via le nouveau token w-matrix-cell (120px, tailwind.config.shared.js) remplacant min-w-matrix-subj, tuile h-8->h-7 (w-full conserve = remplit la colonne fixe -> tuiles identiques) ; max-w-matrix-subj du nom abrege conserve. Rayon de souffle = TOUTES les apps (fond + chiffres vivent dans @gs/ui / tailwind.config.shared.js). AUCUN nouveau composant @gs/ui (pas de DS-013 neuf), aucune mutation de palette/typo (slate/brand/Option B intacts), zero DB/Azure. Mock contrat : docs/standards/mockups/CONSOLE-UG-POLISH-EPURE-MOCK-2026-06-17.html.",
    "2.3.2 (2026-06-17) — MASTER-UG-GABARIT-EDITOR : l'editeur de gabarit de la Console UG (apps/ug TemplatesView) passe de l'editeur PLAT (BC_KEYS.map + code brut + <select> de role) a un editeur de COMPOSITION lisible — modules GROUPES par section conteneur (noms humains GS_CONTAINERS, ordre GS_BC_KEYS_BY_CONTAINER, sections vides masquees, compteur + « tout cocher la section »), LEGENDE des 3 niveaux cumulatifs en tete (pastille d'intensite + nom + description « inclut le precedent »), ACTIONS groupees (tout cocher (N) / tout decocher + « definir tous les roles a » applique a tous les modules coches), tooltip RICHE par module (GsRichTooltipContent : label + description + chips conteneur/prefixe/statut via GS_COMPONENTS.tooltipChip, declencheur focusable tabIndex=0) et SELECTEUR SEGMENTE de role (role=radiogroup + aria-checked, navigable clavier, segment actif teinte a l'intensite du role — plancher slate -> marque brand-* croissante, comme la heatmap ; inerte si module non coche). Composant segmente INLINE app-local (pas de promotion GsSegmented @gs/ui — chore futur). Buffer {name, roles}, toggleBc (defaut UTILISATEUR), save/clone/archive, note « tampon, pas un lien » et aside liste : INCHANGES (aucune mutation de donnees / API serveur). Tokens brand-*/slate- uniquement (regle #28, theming ADR-059) — zero hex, tailles via l'echelle nommee (text-chip..text-kpi, jamais text-[Npx]). AUCUN token / composant @gs/ui neuf (consommation cote app) — le bump trace le pattern d'ecran. Standard : UI_STANDARDS §4c (« editeur de composition » = groupe par conteneur nomme + legende des niveaux + actions groupees + selecteur segmente de role ; accent = token de marque). Aucune modification de palette ni de typo.",
    "2.3.1 (2026-06-17) — MASTER-ROLEMATRIX-HEATMAP (refonte esthetique B, mock fige David « go B ») : la matrice de roles Console UG passe du rendu badge-dans-grille au rendu « carte de presence » heatmap — chaque cellule attribuee est une TUILE pleine (apps/ug RoleMatrix) dont l'INTENSITE encode le niveau d'acces (UTILISATEUR = plancher neutre slate ; GESTIONNAIRE = tint de marque bg-brand-500/15 ; ADMIN = marque pleine bg-brand-500). Cellule vide = tuile a bordure dashed (le « + » d'attribution au survol). Survol en croix (ligne-module + colonne-« qui » illuminees, outline brand-500/35) purement decoratif, souris-seule (n'altere ni le focus clavier ni l'ordre de tabulation, motion-reduce respecte). RoleBadge (popover « role courant » + journal, LOCAL a apps/ug) re-skinne sur la MEME echelle d'intensite (forme badge conservee, couleurs alignees). Tokens brand-*/slate- uniquement (regle #28, theming ADR-059) — zero hex, zero orange en dur. Tokens de role PARTAGES GS_SEMANTIC_COLORS.role* laisses INTACTS (non consommes par le code — decision de rayon de souffle Phase 0). Logique data INCHANGEE (cle `${rowId}|${bc}`, popover, mode lot, unreachableRows). Standard : UI_STANDARDS §4c (matrice d'etat = heatmap d'intensite + survol en croix).",
    "2.3.0 (2026-06-17) — MASTER-CONSOLE-LISIBILITE (passe lisibilite v1, fin de la « loupe ») : nouvelle echelle de TAILLE typo NOMMEE (GS_TYPOGRAPHY.size = miroir des classes Tailwind fontSize text-chip/meta/field/body/lead/head/title/kpi). Plancher remonte : lecture courante 13-14px, meta 12px, 10px (chip) reserve aux badges secondaires — plus de 10/11px en taille de lecture courante. Le contenu des pages Console (apps/ug pages + components/console) ne porte PLUS aucun text-[Npx] / min-w-[Npx] en dur (regle #28) : tout passe par les classes nommees (text-*, min-w-matrix-*, min-w-popover, w-console-*). RoleMatrix : en-tete de colonne 'text' abrege (code + GsTooltip nom complet, declencheur focusable) + colonnes plus etroites (min-w-matrix-subj). Aucun changement de palette ni des roles hierarchy.* existants (zero ripple admin/hub). Itere LIVE en dev local.",
    "2.2.8 (2026-06-17) — MASTER-ROLEMATRIX-TRANSPOSE (chantier RoleMatrix UI, prompt 2/2) : la matrice de roles de la Console UG est TRANSPOSEE (apps/ug RoleMatrix) — modules en LIGNES groupes par section conteneur (noms humains GS_CONTAINERS, ordre GS_BC_KEYS_BY_CONTAINER), le « qui » (compagnies/membres) en COLONNES, 1re colonne module STICKY. Consomme la fondation P1+CHORE : GsTooltip riche (label + description + chips conteneur/prefixe/statut via GS_COMPONENTS.tooltipChip) remplace les title natifs ; pastille de statut seulement sur les modules non actif (ton GS_BC_KEY_STATUS_META -> slate/amber/violet) ; toggle « masquer les planifies ». GsTooltipProvider monte une fois dans le shell App.tsx. Pas de nouveau token / composant @gs/ui (consommation cote app). Standard : UI_STANDARDS §4c (orientation de matrice d'etat : dimension stable en lignes groupees, sujet variable en colonnes ; jamais 15 modules en colonnes) + §8b (a11y : declencheur de tooltip focusable, tabIndex=0). Aucune modification de palette ni de typo.",
    "2.2.7 (2026-06-17) — CHORE-TOOLTIP-CONTENT-TOKENS : GsRichTooltipContent (@gs/ui) devient 100% token-driven. Nouveaux roles typo GS_TYPOGRAPHY.hierarchy.tooltipTitle / tooltipDescription / tooltipMeta (dark-mode aware, alignes sur la surface claire) + chip canonique GS_COMPONENTS.tooltipChip (ligne meta : conteneur/prefixe/statut, consomme par P2 RoleMatrix). L'entree legacy hierarchy.tooltip (ancien tooltip sombre text-white) etait sans consommateur -> reconciliee surface claire slate. GsRichTooltipContent ne porte plus aucune classe text-slate-* en dur. Refactor de SOURCE (les valeurs des tokens = les classes retirees), aucun changement de comportement. Aucune modification de palette.",
    "2.2.6 (2026-06-17) — MASTER-DS-TOOLTIP-CONTAINERS (fondation chantier RoleMatrix, prompt 1/2) : nouvelle primitive GsTooltip + GsTooltipProvider + GsRichTooltipContent dans @gs/ui (sur @radix-ui/react-tooltip). Surface 2026 = token GS_COMPONENTS.tooltip (fond clair eleve en mode clair, slate-800 en sombre, bordure hairline, ombre shadow-lg, coins rounded-lg, largeur max 240px, texte 13px) + tooltipArrow ; keyframe animate-tooltip-in (fade + 2px translate, neutralisee par motion-reduce) ajoutee a tailwind.config.shared ; ouverture 200/0 ms groupe ; a11y focus clavier + tactile + role=tooltip geres par Radix. Standard UI_STANDARDS §8b « Tooltips » (regle : aucun title natif informatif -> GsTooltip). Cote donnees, @gs/auth-multi-tenant/bc-keys enrichi (GS_CONTAINERS, champ description par module, GS_BC_KEY_STATUS_META, GS_BC_KEYS_BY_CONTAINER) pour le tooltip riche que P2 branchera. Livraison inerte : aucun consommateur encore cable -> aucun changement visuel. Aucune modification de palette ni de typo.",
    "2.2.5 (2026-06-15) — MASTER-UG-OVERVIEW-IA : pattern d'accueil de console acte dans UI_STANDARDS §4c (section de nav metier NOMMEE + « console = Vue d'ensemble en porte d'entree » : KPIs d'etat + points d'entree + activite recente). Console UG / = Vue d'ensemble (agregat lecture seule). Carte KPI locale OverviewKpi (apps/ug ; KpiCard @gs/ui manque le slot sous-contenu + tons amber/violet, variant 'orange' = marque). Aucun changement de palette, de typo ni de composant @gs/ui.",
    "2.2.4 (2026-06-15) — CHORE-COMPONENT-CATALOG-RECONCILE : COMPONENT_CATALOG.md deserance (en-tete 2.1.6 -> 2.2.4) et complete de la vague de juin absente (GsFacetFilter, cluster chrome GsNotificationBell + GsFeedbackButton, Shell V2 GsLoginScreen/GsForbiddenScreen/GsProgressBar/GsCategoryBanner, branding ADR-059 BrandingProvider/useBranding + deriveBrandScale/hexToRgbChannels/BRAND_SCALE_STEPS, useVersionCheck) ; sections locales perimees retirees (apps/inventaire + apps/fit decommissionnees). Completude du catalogue desormais verifiee en CI (design:lint DS-013, miroir DS-009) : tout export composant/hook/provider de packages/ui/src/index.ts DOIT avoir une fiche ### Nom. Aucun changement de palette, de typo ni de composant.",
    "2.2.3 (2026-06-15) — MASTER-DS-CHROME-FEEDBACK : nouveau GsFeedbackButton (bulle acces Commentaires dans le header, frere de GsNotificationBell : meme gabarit rounded-md p-1.5 + slate hover, capability-aware /api/feedback, SANS badge — decision David v1, pas de double-compte avec la cloche). Cluster header = cloche PUIS bulle. Vue Commentaires (ug/inv) passee en PLEINE LARGEUR (retrait mx-auto max-w-4xl). Aucun changement de palette ni de typo.",
    "2.2.2 (2026-06-15) — MASTER-DS-POLISH-PASS : fond de page bg-[#fbfbfb] -> token bg-slate-50 (cartes bg-white contrastent ; applique GsLayout/GsHeader/GsLoginScreen/GsForbiddenScreen + admin + standards/style-guide) ; GsSearchInput = champ de recherche canonique + variant 'inline' (en-tetes etroits, sans boite, filet bas) ; GsFacetFilter : icone entonnoir (lucide Filter) sur le declencheur (etat actif slate = wayfinding). Aucun changement de palette ni de typo.",
    "2.2.1 (2026-06-15) — Renaissance INV (BLITZ-INV-SCAFFOLD) : MobileBottomNav -> GsMobileBottomNav (renomme + alias deprecated 1 release) enrichi — pastille badge (item.badge), FAB central optionnel, prop navLabel (a11y, i18n par prop), test colocate. Premiere implementation reelle d'un composant mobile partage (SPEC-INV D6, regle « composant partage a la premiere implementation »). Aucun changement de palette ni de typo.",
    "2.2.0 (2026-06-10) — V2 Sweet spot : theming runtime ADR-059 (brand-* = variables CSS --brand-N-rgb, deriveBrandScale + BrandingProvider, pilote Console UG, fallback :root GS 2026 #d51224), Inter self-hostée (@fontsource-variable/inter, fontFamily.sans), nouveaux composants GsLoginScreen/GsForbiddenScreen/GsProgressBar/GsCategoryBanner ; discipline CTA slate reconfirmée — brand = wayfinding seulement. Aucun changement de palette slate ni de la typo Option B.",
    '2.1.6 (2026-05-01) — ECOSYSTEM_SYNC post-brainstorm BD : MobileBottomNav documenté dans COMPONENT_CATALOG, style-guide aligné v2.1.6, COMPONENT_CATALOG header sync 2.1.2 → 2.1.6, changelog tokens rempli (2.1.3/2.1.4/2.1.5). Aucun changement de palette ni de composant.',
    '2.1.5 (2026-04-28 PM) — Brand cleanup global : logos officiels (NoTagline + DarkMode + icônes adaptatives). UI_STANDARDS §2 + sync:assets. Aucun changement de palette tokens.',
    '2.1.4 (2026-04-27) — UX api-portail : bouton « Connexion gestionnaire » amélioré (visibilité, contraste, touch target ≥ 44px) en remplacement du footer link. Pas de modification tokens.',
    '2.1.3 (2026-04-24) — Première application du DS sur page SSR public-facing (api-portail `/p/:qrCode`). Tokens DS dupliqués en CSS variables (`--gs-brand-500`, `--gs-slate-*`) puisque pas de Tailwind compilé en SSR. Patterns SVG icons inline lucide-style. Pas de modification tokens.ts ni packages/ui.',
    '2.1.2 (2026-04-17) — Composants @gs/ui : GsErrorPage (routes erreur HTTP), GsVersionBanner + hook useVersionCheck ; hub : PwaUpdateBanner (PWA NetworkFirst + consentement). Docs COMPONENT_CATALOG + DESIGN_SYSTEM synchronisés. Aucun changement de palette tokens.',
    '2.1.1 (2026-04-09) — Docs standards + style-guide alignés sur tokens (aucun changement de palette). COMPONENT_CATALOG : GsSearchInput. Réf. architecture / ETL / MCP en changelog projet.',
    '2.1.0 (2026-04-01) — Couleur principale orange #f97316 → Rouge GS #D92231 (Pantone 032). Token brand-* ajouté dans tailwind.config.shared. bg-orange-* banni → bg-brand-500.',
    '2.0.1 (2026-03-31) — qtyMinus/qtyPlus compact 32px slate ; btnAction/btnActionMobile slate ; btnPrimary/btnPrimaryMobile DEPRECATED',
    '2.0.0 (2026-03-31) — Typographie Option B appliquée (PageHeader, KpiCard, DataTable + pages inventaire ciblées)',
    '1.5.0 (2026-03-29) — @gs/assets + sync:assets ; logo dark sidebar (`headerLogoSrcDark`) ; GsUserMenu à droite si sidebar rétractée',
    '1.4.0 (2026-03-30) — Dark mode GS-Inventaire (pages + index.css) ; GsSidebar rétractable (localStorage)',
    '1.3.0 (2026-03-30) — GsIconButton dans @gs/ui (icône + tooltip) — standard header actions',
    '1.2.0 (2026-03-30) — GS_LAYOUTS.financialGrid + fix buildQueryString CSV + FinancialPage compact',
    '1.1.0 (2026-03-30) — Promotion Skeleton, QueryError, ConfirmDialog vers @gs/ui',
    '1.0.0 (2026-03-30) — Version initiale basée sur GS-Inventaire + PoC + Google AI Studio',
  ],
} as const;
